import { useEffect, useState, type ReactNode } from 'react'
import { Icon } from '../../components/Icon'
import {
  androidBrowser,
  installPromptState,
  iosBrowser,
  onInstallPromptChange,
  promptInstall,
  type InstallPlatform,
} from './installGate'

function useInstallPrompt() {
  const [state, setState] = useState(installPromptState)
  useEffect(() => onInstallPromptChange(() => setState(installPromptState())), [])
  return state
}

function Steps({ children }: { children: ReactNode }) {
  return <ol className="install-steps">{children}</ol>
}

function CopyLink() {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      className="button"
      onClick={() => {
        void navigator.clipboard
          ?.writeText(location.origin + '/')
          .then(() => setCopied(true))
          .catch(() => undefined)
      }}
    >
      <Icon name={copied ? 'check' : 'copy'} />
      {copied ? 'Link copied' : 'Copy link'}
    </button>
  )
}

function IosSteps() {
  const browser = iosBrowser()
  if (browser === 'in-app') {
    return (
      <>
        <p>This page is open inside another app, which can't add BOSS Chat to your Home Screen. Open it in Safari first.</p>
        <Steps>
          <li>Copy the link below, or use this app's menu and choose <strong>Open in Safari</strong>.</li>
          <li>Paste it into Safari, then follow the steps shown there.</li>
        </Steps>
        <CopyLink />
      </>
    )
  }
  return (
    <>
      <p>On iPhone and iPad, BOSS Chat runs from your Home Screen. That's also how it gets notifications.</p>
      <Steps>
        <li>
          Tap <strong>Share</strong> <Icon name="share" />{' '}
          {browser === 'safari' ? (
            <>in Safari's toolbar. If you don't see it, tap <strong>⋯</strong> first.</>
          ) : (
            <>in this browser's address bar or menu.</>
          )}
        </li>
        <li>
          Scroll down and tap <strong>Add to Home Screen</strong>.
        </li>
        <li>
          Leave <strong>Open as Web App</strong> on if it's shown, then tap <strong>Add</strong>.
        </li>
        <li>
          Open <strong>BOSS Chat</strong> from your Home Screen and sign in there.
        </li>
      </Steps>
      {browser === 'other' ? <p className="install-note">Don't see Add to Home Screen? Open this page in Safari and try again.</p> : null}
    </>
  )
}

function AndroidSteps() {
  const { canPrompt, installed } = useInstallPrompt()
  const browser = androidBrowser()
  if (installed) {
    return (
      <>
        <p>BOSS Chat is installed.</p>
        <Steps>
          <li>
            Open <strong>BOSS Chat</strong> from your home screen or app list and sign in there.
          </li>
        </Steps>
      </>
    )
  }
  if (browser === 'in-app') {
    return (
      <>
        <p>This page is open inside another app, which can't install BOSS Chat. Open it in Chrome first.</p>
        <Steps>
          <li>Copy the link below, or use this app's menu and choose <strong>Open in Chrome</strong> or <strong>Open in browser</strong>.</li>
          <li>Paste it into Chrome, then follow the steps shown there.</li>
        </Steps>
        <CopyLink />
      </>
    )
  }
  return (
    <>
      <p>On Android, BOSS Chat runs as an app on your home screen.</p>
      {canPrompt ? (
        <button type="button" className="button primary" onClick={() => void promptInstall()}>
          <Icon name="download" />
          Install BOSS Chat
        </button>
      ) : null}
      <Steps>
        {browser === 'samsung' ? (
          <li>
            Tap the menu <strong>☰</strong>, then <strong>Add page to</strong> and <strong>Home screen</strong>.
          </li>
        ) : (
          <li>
            {canPrompt ? 'Or tap' : 'Tap'} the menu <strong>⋮</strong>, then <strong>Install app</strong> or <strong>Add to Home screen</strong>.
          </li>
        )}
        <li>
          Tap <strong>Install</strong> to confirm.
        </li>
        <li>
          Open <strong>BOSS Chat</strong> from your home screen or app list and sign in there.
        </li>
      </Steps>
      {browser === 'other' ? <p className="install-note">If your browser has no install option, open this page in Chrome.</p> : null}
    </>
  )
}

/** Shown instead of the app in a phone's browser: how to install it, for this kind of phone. */
export function InstallScreen({ platform }: { platform: InstallPlatform }) {
  return (
    <main className="sign-in install-screen">
      <div className="sign-in-card">
        <img className="app-mark" src="/icons/icon-192.png" alt="" width={72} height={72} />
        <h1 className="chat-wordmark">
          BOSS <span>Chat</span>
        </h1>
        <h2 className="install-title">Add BOSS Chat to your {platform === 'ios' ? 'Home Screen' : 'home screen'}</h2>
        {platform === 'ios' ? <IosSteps /> : <AndroidSteps />}
      </div>
    </main>
  )
}
